import { useCallback, useRef } from 'react';

type SoundType = 'startRecording' | 'stopRecording' | 'success' | 'error' | 'complete' | 'welcome' | 'click' | 'goalReached';

interface UseSoundsReturn {
  playSound: (type: SoundType) => void;
  setSoundEnabled: (enabled: boolean) => void;
  isSoundEnabled: () => boolean;
}

const SOUND_ENABLED_KEY = 'litrlly_kids_sounds_enabled';

const useSounds = (): UseSoundsReturn => {
  const audioContextRef = useRef<AudioContext | null>(null);

  const isSoundEnabled = useCallback((): boolean => {
    const stored = localStorage.getItem(SOUND_ENABLED_KEY);
    return stored === null ? true : stored === 'true';
  }, []);

  const setSoundEnabled = useCallback((enabled: boolean): void => {
    localStorage.setItem(SOUND_ENABLED_KEY, String(enabled));
  }, []);

  const getAudioContext = useCallback((): AudioContext => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
    return audioContextRef.current;
  }, []);

  const playChime = useCallback((frequencies: number[], duration: number, volume: number = 0.1): void => {
    const ctx = getAudioContext();

    if (ctx.state === 'suspended') {
      ctx.resume();
    }

    frequencies.forEach((freq, index) => {
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);

      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(freq, ctx.currentTime);

      const startTime = ctx.currentTime + (index * 0.08);
      gainNode.gain.setValueAtTime(0, startTime);
      gainNode.gain.linearRampToValueAtTime(volume, startTime + 0.02);
      gainNode.gain.exponentialRampToValueAtTime(0.001, startTime + duration);

      oscillator.start(startTime);
      oscillator.stop(startTime + duration);
    });
  }, [getAudioContext]);

  const playSound = useCallback((type: SoundType): void => {
    if (!isSoundEnabled()) return;

    try {
      const ctx = getAudioContext();
      if (ctx.state === 'suspended') ctx.resume();

      switch (type) {
        case 'startRecording': {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.type = 'sine';
          osc.frequency.setValueAtTime(440, ctx.currentTime);
          osc.frequency.exponentialRampToValueAtTime(880, ctx.currentTime + 0.15);
          gain.gain.setValueAtTime(0.12, ctx.currentTime);
          gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.2);
          osc.start(ctx.currentTime);
          osc.stop(ctx.currentTime + 0.2);
          break;
        }

        case 'stopRecording': {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.type = 'sine';
          osc.frequency.setValueAtTime(660, ctx.currentTime);
          osc.frequency.exponentialRampToValueAtTime(440, ctx.currentTime + 0.12);
          gain.gain.setValueAtTime(0.12, ctx.currentTime);
          gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.15);
          osc.start(ctx.currentTime);
          osc.stop(ctx.currentTime + 0.15);
          break;
        }

        case 'success':
          playChime([523.25, 659.25, 783.99], 0.3, 0.12);
          break;

        case 'complete':
          playChime([440, 554.37, 659.25], 0.35, 0.1);
          break;

        case 'error': {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.type = 'sine';
          osc.frequency.setValueAtTime(220, ctx.currentTime);
          gain.gain.setValueAtTime(0.1, ctx.currentTime);
          gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.25);
          osc.start(ctx.currentTime);
          osc.stop(ctx.currentTime + 0.25);
          break;
        }

        case 'welcome':
          // Cheerful welcoming melody - C5, E5, G5, C6
          playChime([523.25, 659.25, 783.99, 1046.5], 0.4, 0.12);
          break;

        case 'click': {
          // Short click feedback
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.type = 'sine';
          osc.frequency.setValueAtTime(800, ctx.currentTime);
          gain.gain.setValueAtTime(0.08, ctx.currentTime);
          gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.05);
          osc.start(ctx.currentTime);
          osc.stop(ctx.currentTime + 0.05);
          break;
        }

        case 'goalReached': {
          // Celebratory fanfare for reaching daily goal!
          const notes = [523.25, 523.25, 523.25, 698.46, 783.99, 1046.5]; // C5, C5, C5, F5, G5, C6
          const delays = [0, 0.1, 0.2, 0.35, 0.45, 0.6];
          notes.forEach((freq, index) => {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.type = 'sine';
            const startTime = ctx.currentTime + delays[index];
            osc.frequency.setValueAtTime(freq, startTime);
            gain.gain.setValueAtTime(0, startTime);
            gain.gain.linearRampToValueAtTime(0.15, startTime + 0.02);
            gain.gain.exponentialRampToValueAtTime(0.001, startTime + 0.35);
            osc.start(startTime);
            osc.stop(startTime + 0.35);
          });
          break;
        }
      }
    } catch (err) {
      console.warn('Sound playback failed:', err);
    }
  }, [getAudioContext, isSoundEnabled, playChime]);

  return { playSound, setSoundEnabled, isSoundEnabled };
};

export default useSounds;
